import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  Activity,
  NaturalFeature,
  NaturalFeatureKind,
  RoutePublic,
  Seasonality,
} from '@xingroute/schema'
import { SEASONS, seasonDisplayLabel } from '../RouteSeasonalityBlock.js'
import { Input } from '../ui/Field.js'
import { IconChevronDown, IconRefresh, IconPlus } from '../ui/IconButton.js'
import { SELECTABLE_ACTIVITIES, isSelectableActivity } from '../../lib/selectableActivities.js'

// EditorTagBar — unified, editable metadata bar for the route editor.
//
// Route metadata is rendered as a horizontal row of "chips"; legacy activities
// are read-only. The bar replaces the previous stack of
// per-field Form rows (which produced one bordered card per metadatum and
// made the editor visually heavy). Two chip flavours coexist:
//
//   1. Enum chips (status / activity / difficulty) — pill-styled
//      `<select>`. The user picks an enum value directly without leaving
//      the bar. Status uses brand-primary tint to distinguish "publish
//      state" from neutral facts.
//   2. Structured chips (region / natural features) — pill-styled
//      `<button>` that shows the current summary value (e.g.
//      "台北市·士林区 ▾"). Clicking expands an inline editor below the bar
//      where the user fills in structured fields and (for region) triggers
//      Tianditu re-detection. Esc or click-outside collapses.
//
// A trailing `＋ 添加标签` placeholder chip reserves layout space for
// future tag types (season / surface / equipment / permit / custom tags)
// without requiring a structural rewrite when they land. Clicking it
// today emits an `onAddSoon` toast via the parent.
//
// Keeping the bar self-contained means RouteEditor's intro-summary
// section is now: bare-title `<input>` → `<EditorTagBar>` →
// `<KpiStrip>`, mirroring the read view's single-card density.

type Difficulty = 'easy' | 'moderate' | 'hard' | 'expert'
type RouteTypeValue = 'loop' | 'point_to_point' | 'out_and_back'
type Region = RoutePublic['region']
type Status = RoutePublic['status']
type NaturalFeatures = NonNullable<RoutePublic['natural_features']>

const NATURAL_FEATURE_KINDS: NaturalFeatureKind[] = [
  'mountain',
  'lake',
  'river',
  'waterfall',
  'pass',
  'other',
]

export interface EditorTagBarProps {
  status: Status
  activities: Activity[]
  difficulty: Difficulty | ''
  routeType: RouteTypeValue | ''
  region: Region | undefined
  naturalFeatures: NaturalFeatures
  seasonality?: Seasonality
  onChangeStatus: (next: Status) => void
  onChangeActivities: (next: Activity[]) => void
  onChangeDifficulty: (next: Difficulty | '') => void
  onChangeRouteType: (next: RouteTypeValue | '') => void
  onPatchRegion: (patch: Partial<NonNullable<Region>>) => void
  onChangeNaturalFeatures: (next: NaturalFeatures) => void
  onChangeSeasonality?: (next: Seasonality) => void
  /** Tianditu reverse-geocode. Parent decides whether geometry is
   *  available; passing `undefined` disables the re-detect button. */
  onDetectRegion?: () => void
  regionDetecting?: boolean
  regionDetectAvailable?: boolean
  /** Whether to show the admin-only Archived status option. */
  adminStatusOptions?: boolean
  /** Show or hide the topbar status select. Status is always editable
   *  through the chip; pass `false` here only if you keep an external
   *  status control. */
  showStatusChip?: boolean
  onAddSoon?: () => void
  /** Used to gate the editor's right-rail behaviour from tests. */
  testid?: string
}

type ExpandedKey = 'region' | 'features' | null

export function EditorTagBar({
  status,
  activities,
  difficulty,
  routeType,
  region,
  naturalFeatures,
  seasonality,
  onChangeStatus,
  onChangeActivities,
  onChangeDifficulty,
  onChangeRouteType,
  onPatchRegion,
  onChangeNaturalFeatures,
  onChangeSeasonality,
  onDetectRegion,
  regionDetecting = false,
  regionDetectAvailable = false,
  adminStatusOptions = false,
  showStatusChip = true,
  onAddSoon,
  testid = 'editor-tagbar',
}: EditorTagBarProps) {
  const { t } = useTranslation('common')
  const [expanded, setExpanded] = useState<ExpandedKey>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Esc collapses the inline editor.
  useEffect(() => {
    if (!expanded) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setExpanded(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expanded])

  // Click-outside collapses the inline editor.
  useEffect(() => {
    if (!expanded) return
    function onClick(event: MouseEvent) {
      const node = containerRef.current
      if (!node) return
      if (!node.contains(event.target as Node)) setExpanded(null)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [expanded])

  const regionSummary = formatRegionSummary(region) || t('editor.tagbar.regionSummaryEmpty')
  const naturalFeaturesSummary =
    formatNaturalFeaturesSummary(naturalFeatures, t) ||
    t('editor.tagbar.naturalFeaturesSummaryEmpty')

  function toggle(key: ExpandedKey) {
    setExpanded((prev) => (prev === key ? null : key))
  }

  return (
    <div ref={containerRef} className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="route tags">
        {showStatusChip && (
          <ChipSelect
            label={t('editor.tagbar.statusChip')}
            value={status}
            tone="neutral"
            onChange={(value) => onChangeStatus(value as Status)}
            testid="editor-tagbar-status"
          >
            <option value="draft">{t('editor.status.draft')}</option>
            <option value="published">{t('editor.status.published')}</option>
            {adminStatusOptions && <option value="archived">{t('editor.status.archived')}</option>}
          </ChipSelect>
        )}

        <fieldset
          className="inline-flex h-[26px] items-center gap-0"
          data-testid="editor-tagbar-activity"
        >
          <span className="flex h-full items-center rounded-l-full border border-r-0 border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 text-[11px] font-medium text-[var(--xr-text-muted)]">
            {t('editor.tagbar.activityChip')}
          </span>
          <div className="flex h-full items-center gap-0.5 rounded-r-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-1">
            {SELECTABLE_ACTIVITIES.map((act) => {
              const active = activities.includes(act)
              const isLast = activities.length === 1 && active
              return (
                <button
                  key={act}
                  type="button"
                  disabled={isLast}
                  onClick={() => {
                    const next = active ? activities.filter((a) => a !== act) : [...activities, act]
                    onChangeActivities(next)
                  }}
                  className={
                    active
                      ? 'h-5 rounded-full bg-[var(--xr-brand-primary-soft)] px-1.5 text-[11px] font-medium text-[var(--xr-brand-primary)]'
                      : 'h-5 rounded-full px-1.5 text-[11px] font-medium text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface)]'
                  }
                  aria-pressed={active}
                  data-testid={`editor-tagbar-activity-${act}`}
                >
                  {t(`editor.activity${act.charAt(0).toUpperCase() + act.slice(1)}`)}
                </button>
              )
            })}
            {activities
              .filter((act) => !isSelectableActivity(act))
              .map((act) => (
                <span
                  key={act}
                  className="px-1.5 text-[11px] text-[var(--xr-text-muted)]"
                  title={t('editor.legacyActivity')}
                  data-testid={`editor-tagbar-legacy-activity-${act}`}
                >
                  {t(`enumLabel_activity_${act}`)}
                </span>
              ))}
          </div>
        </fieldset>

        <ChipSelect
          label={t('editor.tagbar.difficultyChip')}
          value={difficulty}
          onChange={(value) => onChangeDifficulty(value as Difficulty | '')}
          testid="editor-tagbar-difficulty"
        >
          <option value="easy">{t('editor.difficultyEasy')}</option>
          <option value="moderate">{t('editor.difficultyModerate')}</option>
          <option value="hard">{t('editor.difficultyHard')}</option>
          <option value="expert">{t('editor.difficultyExpert')}</option>
        </ChipSelect>

        <ChipSelect
          label={t('filterRouteType')}
          value={routeType}
          onChange={(value) => onChangeRouteType(value as RouteTypeValue | '')}
          testid="editor-tagbar-route-type"
        >
          <option value="loop">{t('enumLabel_route_type_loop' as never)}</option>
          <option value="point_to_point">
            {t('enumLabel_route_type_point_to_point' as never)}
          </option>
          <option value="out_and_back">{t('enumLabel_route_type_out_and_back' as never)}</option>
        </ChipSelect>

        <ChipButton
          label={t('editor.tagbar.regionChip')}
          summary={regionSummary}
          expanded={expanded === 'region'}
          ariaLabel={t(
            expanded === 'region' ? 'editor.tagbar.collapseAria' : 'editor.tagbar.expandAria',
            { label: t('editor.tagbar.regionChip') },
          )}
          onClick={() => toggle('region')}
          testid="editor-tagbar-region"
          controlsId="editor-tagbar-region-editor"
        />

        <ChipButton
          label={t('editor.tagbar.naturalFeaturesChip')}
          summary={naturalFeaturesSummary}
          expanded={expanded === 'features'}
          ariaLabel={t(
            expanded === 'features' ? 'editor.tagbar.collapseAria' : 'editor.tagbar.expandAria',
            { label: t('editor.tagbar.naturalFeaturesChip') },
          )}
          onClick={() => toggle('features')}
          testid="editor-tagbar-natural-features"
          controlsId="editor-tagbar-natural-features-editor"
        />

        {onChangeSeasonality && (
          <SeasonMultiChip
            bestSeasons={seasonality?.best_seasons ?? []}
            onChange={(next) =>
              onChangeSeasonality({
                best_seasons: next as Seasonality['best_seasons'],
                closed_periods: seasonality?.closed_periods ?? [],
              })
            }
          />
        )}
      </div>

      {expanded === 'region' && (
        <ChipEditor id="editor-tagbar-region-editor" onDone={() => setExpanded(null)}>
          <Input
            value={region?.province ?? ''}
            placeholder={t('editor.regionProvince')}
            onChange={(event) => onPatchRegion({ province: event.target.value })}
            data-testid="intro-summary-region-province"
            aria-label={t('editor.regionProvince')}
            className="min-w-[5rem] flex-1 !px-2 !py-1 !text-xs"
          />
          <Input
            value={region?.city ?? ''}
            placeholder={t('editor.regionCity')}
            onChange={(event) => onPatchRegion({ city: event.target.value })}
            data-testid="intro-summary-region-city"
            aria-label={t('editor.regionCity')}
            className="min-w-[5rem] flex-1 !px-2 !py-1 !text-xs"
          />
          <Input
            value={region?.district ?? ''}
            placeholder={t('editor.regionDistrict')}
            onChange={(event) => onPatchRegion({ district: event.target.value })}
            data-testid="intro-summary-region-district"
            aria-label={t('editor.regionDistrict')}
            className="min-w-[5rem] flex-1 !px-2 !py-1 !text-xs"
          />
          {onDetectRegion && (
            <button
              type="button"
              onClick={onDetectRegion}
              disabled={regionDetecting || !regionDetectAvailable}
              title={
                regionDetectAvailable
                  ? t('editor.regionDetectAuto')
                  : t('editor.regionDetectMissing')
              }
              className="inline-flex flex-none items-center gap-1 rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 py-0.5 text-[11px] text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="intro-summary-region-detect"
            >
              <IconRefresh size={11} />
              {regionDetecting ? t('editor.regionDetecting') : t('editor.regionDetect')}
            </button>
          )}
        </ChipEditor>
      )}

      {expanded === 'features' && (
        <ChipEditor id="editor-tagbar-natural-features-editor" onDone={() => setExpanded(null)}>
          <NaturalFeatureInput
            value={naturalFeatures}
            onChange={onChangeNaturalFeatures}
            addPlaceholder={t('editor.tagbar.naturalFeatureAddPlaceholder')}
            removeAria={t('editor.tagbar.naturalFeatureRemoveAria')}
          />
        </ChipEditor>
      )}
    </div>
  )
}

// Multi-tag editor for `route.natural_features` sibling `{kind, name}`
// entries. Each tag renders as a chip prefixed by the localized kind label;
// new entries pick a kind from the dropdown and type a name.
function NaturalFeatureInput({
  value,
  onChange,
  addPlaceholder,
  removeAria,
}: {
  value: NaturalFeatures
  onChange: (next: NaturalFeatures) => void
  addPlaceholder: string
  removeAria: string
}) {
  const { t } = useTranslation('common')
  const [draftName, setDraftName] = useState('')
  const [draftKind, setDraftKind] = useState<NaturalFeatureKind>('mountain')

  function add() {
    const name = draftName.trim()
    if (!name) return
    if (value.some((f) => f.kind === draftKind && f.name === name)) {
      setDraftName('')
      return
    }
    if (value.length >= 16) return
    const next: NaturalFeature = { kind: draftKind, name }
    onChange([...value, next])
    setDraftName('')
  }

  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx))
  }

  return (
    <div
      className="flex flex-1 flex-wrap items-center gap-1"
      data-testid="intro-summary-natural-features"
    >
      {value.map((feature, idx) => (
        <span
          key={`${feature.kind}-${feature.name}-${idx}`}
          className="inline-flex items-center gap-1 rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-2 py-0.5 text-[11px] text-[var(--xr-text-primary)]"
          data-testid={`intro-summary-natural-feature-${idx}`}
        >
          <span className="text-[10px] tracking-wide opacity-60">
            {t(`editor.naturalFeatureKind.${feature.kind}`)}
          </span>
          <span aria-hidden="true" className="opacity-40">
            ·
          </span>
          <span>{feature.name}</span>
          <button
            type="button"
            onClick={() => remove(idx)}
            className="text-[var(--xr-text-muted)] hover:text-[var(--xr-danger)]"
            aria-label={`${removeAria}: ${feature.name}`}
            data-testid={`intro-summary-natural-feature-remove-${idx}`}
          >
            ×
          </button>
        </span>
      ))}
      <label
        className="inline-flex items-center"
        aria-label={t('editor.tagbar.naturalFeatureKind')}
      >
        <span className="sr-only">{t('editor.tagbar.naturalFeatureKind')}</span>
        <select
          value={draftKind}
          onChange={(event) => setDraftKind(event.target.value as NaturalFeatureKind)}
          className="appearance-none cursor-pointer rounded-l-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 py-0.5 pr-5 text-[11px] text-[var(--xr-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--xr-brand-primary-soft)]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M3 4.5L6 7.5L9 4.5'/></svg>\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0.3rem center',
            backgroundSize: '0.6rem',
          }}
          data-testid="intro-summary-natural-feature-kind"
        >
          {NATURAL_FEATURE_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`editor.naturalFeatureKind.${kind}`)}
            </option>
          ))}
        </select>
      </label>
      <Input
        value={draftName}
        onChange={(event) => setDraftName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            add()
          }
        }}
        onBlur={() => add()}
        placeholder={addPlaceholder}
        className="min-w-[10rem] flex-1 rounded-l-none border-l-0 !px-2 !py-0.5 !text-[11px]"
        aria-label={addPlaceholder}
        data-testid="intro-summary-natural-feature-add"
      />
    </div>
  )
}

function ChipSelect({
  label,
  value,
  onChange,
  tone = 'neutral',
  children,
  testid,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  tone?: 'neutral' | 'brand'
  children: ReactNode
  testid: string
}) {
  const toneCls =
    tone === 'brand'
      ? 'bg-[var(--xr-brand-primary-soft)] text-[var(--xr-brand-primary)] border-[var(--xr-brand-primary-soft)]'
      : 'bg-[var(--xr-bg-surface-elev)] text-[var(--xr-text-primary)] border-[var(--xr-border-default)]'
  return (
    <label className="inline-flex h-[26px] items-center gap-0" aria-label={label}>
      <span
        className={`flex h-full items-center rounded-l-full border border-r-0 px-2 text-[11px] font-medium text-[var(--xr-text-muted)] ${tone === 'brand' ? 'border-[var(--xr-brand-primary-soft)] bg-[var(--xr-brand-primary-soft)]/50' : 'border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]'}`}
      >
        {label}
      </span>
      <select
        id={testid}
        name={testid}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`h-full appearance-none cursor-pointer rounded-r-full border px-2 pr-6 text-[11px] font-medium ${toneCls} focus:outline-none focus:ring-2 focus:ring-[var(--xr-brand-primary-soft)]`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M3 4.5L6 7.5L9 4.5'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.4rem center',
          backgroundSize: '0.7rem',
        }}
        data-testid={testid}
      >
        {children}
      </select>
    </label>
  )
}

function ChipButton({
  label,
  summary,
  expanded,
  ariaLabel,
  onClick,
  testid,
  controlsId,
}: {
  label: string
  summary: string
  expanded: boolean
  ariaLabel: string
  onClick: () => void
  testid: string
  controlsId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-controls={controlsId}
      aria-label={ariaLabel}
      className="inline-flex h-[26px] items-center gap-0"
      data-testid={testid}
    >
      <span
        className={`flex h-full items-center rounded-l-full border border-r-0 px-2 text-[11px] font-medium text-[var(--xr-text-muted)] ${expanded ? 'border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)]/50' : 'border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]'}`}
      >
        {label}
      </span>
      <span
        className={`flex h-full items-center gap-1 rounded-r-full border px-2 text-[11px] font-medium ${expanded ? 'border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)] text-[var(--xr-brand-primary)]' : 'border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] text-[var(--xr-text-primary)]'} hover:bg-[var(--xr-bg-surface)]`}
      >
        <span className="max-w-[8rem] truncate">{summary}</span>
        <IconChevronDown size={10} />
      </span>
    </button>
  )
}

function ChipAdd({ onClick, label }: { onClick?: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--xr-border-default)] px-3 py-1 text-xs text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface-elev)]"
      data-testid="editor-tagbar-add"
    >
      <IconPlus size={10} />
      <span>{label}</span>
    </button>
  )
}

function ChipEditor({
  id,
  onDone,
  children,
}: {
  id: string
  onDone: () => void
  children: ReactNode
}) {
  const { t } = useTranslation('common')
  return (
    <div
      id={id}
      role="group"
      className="flex flex-wrap items-center gap-1.5 rounded-r-[var(--xr-radius-md)] border-l-2 border-dashed border-[var(--xr-brand-primary)] bg-[var(--xr-bg-surface-elev)] py-2 pl-3 pr-2"
      data-testid={`${id}`}
    >
      {children}
      <button
        type="button"
        onClick={onDone}
        className="ml-auto rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 py-0.5 text-[11px] font-medium text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)]"
      >
        {t('editor.tagbar.done')}
      </button>
    </div>
  )
}

function formatRegionSummary(region: Region | undefined): string {
  if (!region) return ''
  const parts = [region.province, region.city, region.district].filter((part): part is string =>
    Boolean(part && part.length > 0),
  )
  return parts.join(' · ')
}

function formatNaturalFeaturesSummary(
  features: NaturalFeatures,
  t: (key: string) => string,
): string {
  if (!features.length) return ''
  if (features.length <= 3) {
    return features.map((f) => f.name).join('、')
  }
  return `${features
    .slice(0, 2)
    .map((f) => f.name)
    .join('、')} +${features.length - 2} ${t('editor.tagbar.naturalFeaturesMore')}`
}

function SeasonMultiChip({
  bestSeasons,
  onChange,
}: {
  bestSeasons: string[]
  onChange: (next: string[]) => void
}) {
  const { i18n } = useTranslation('common')
  const isZh = i18n.language?.startsWith('zh')
  const selected = new Set(bestSeasons)
  const summary =
    bestSeasons.length === 0
      ? isZh
        ? '全年'
        : 'All year'
      : bestSeasons
          .map((season) =>
            seasonDisplayLabel(season as Seasonality['best_seasons'][number], i18n.language),
          )
          .join(' ')

  return (
    <div
      className="inline-flex h-[26px] items-center gap-0"
      data-testid="editor-tagbar-seasonality"
    >
      <span className="flex h-full items-center rounded-l-full border border-r-0 border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 text-[11px] font-medium text-[var(--xr-text-muted)]">
        {isZh ? '季节' : 'Season'}
      </span>
      <div className="flex h-full items-center gap-0.5 rounded-r-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-1">
        {SEASONS.map((season) => {
          const active = selected.has(season)
          const label = seasonDisplayLabel(season, i18n.language)
          return (
            <button
              key={season}
              type="button"
              onClick={() => {
                const next = new Set(selected)
                if (active) next.delete(season)
                else next.add(season)
                onChange(SEASONS.filter((candidate) => next.has(candidate)))
              }}
              className={
                active
                  ? 'h-5 min-w-5 rounded-full bg-[var(--xr-brand-primary-soft)] px-1.5 text-[11px] font-medium text-[var(--xr-brand-primary)]'
                  : 'h-5 min-w-5 rounded-full px-1.5 text-[11px] font-medium text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface)]'
              }
              aria-pressed={active}
              aria-label={isZh ? `${label}季` : label}
              title={label}
            >
              {isZh ? label : label.charAt(0)}
            </button>
          )
        })}
      </div>
    </div>
  )
}
